import { TableRenderer, TableRenderType } from "../../shared/components/edit-table/table-renderer";
import { JsonItem } from "../../shared/entities/pick-list/pick-list.entities";
import { getDisplayDate } from "../../shared/scripts/date-functions";



export type CabHubSurveyHistoryRow = {
    SurveyType:
    string;

    MonthYearDisplay:
    string;

    HoursWorked:
    number;

    MetThisMonth:
    boolean;

    SatisfactionRatingId:
    number;

    SupportRequested:
    boolean;

    SubmittedDate:
    string;
};


export type CabHubConnectionDetailsVm = {
    Id:
    number;

    Status:
    string;

    DocumentUploadId:
    string;

    SurveyHistory:
    CabHubSurveyHistoryRow[];

    PickLists:
    JsonItem[];
};


export class CabHubConnectionDetailsPage {

    private readonly surveyHistoryTable:
        CabHubConnectionSurveyHistoryTable;

    constructor(
        private readonly vm:
            CabHubConnectionDetailsVm
    ) {
        this.surveyHistoryTable =
            new CabHubConnectionSurveyHistoryTable(
                this.vm.SurveyHistory ?? [],
                this.vm.PickLists ?? []
            );

        this.initializeStatus();

        this.initializeAgreementDownload();

        this.surveyHistoryTable
            .initialize();
    }


    private initializeStatus():
        void {

        const isActive =
            (this.vm.Status ?? "")
                .trim()
                .toLowerCase() ===
            "active";

        $("#cab-hub-connection-status")
            .addClass(
                isActive
                    ? "cab-edit-table-active-pill"
                    : "cab-detail-table-inactive-pill"
            );
    }


    private async initializeAgreementDownload():
        Promise<void> {

        const uploadId =
            this.vm.DocumentUploadId;

        if (!uploadId) {
            return;
        }

        try {
            const result: any =
                await $.ajax(
                    "/api/upload-service/get-files",
                    {
                        method:
                            "POST",

                        headers: {
                            "RequestVerificationToken":
                                String(
                                    $(
                                        'input[name="__RequestVerificationToken"]'
                                    ).val() ?? ""
                                )
                        },

                        contentType:
                            "application/json; charset=utf-8",

                        data:
                            JSON.stringify({
                                rootFolderPath:
                                    "cab-hub-uploads",

                                uuidArray: [
                                    uploadId
                                ]
                            })
                    }
                );

            const document =
                result?.items?.[0];

            if (!document) {
                $("#cab-hub-agreement-loading")
                    .text(
                        "Agreement document unavailable"
                    );

                return;
            }

            const filePath =
                encodeURI(
                    document.id
                );

            $("#cab-hub-agreement-download")
                .attr(
                    "href",
                    `/api/upload-service/download-file?filePath=${filePath}`
                )
                .attr(
                    "download",
                    document.text
                )
                .text(
                    document.text ||
                    "View agreement document"
                )
                .removeClass(
                    "d-none"
                );

            $("#cab-hub-agreement-loading")
                .remove();
        }
        catch (error) {
            console.error(
                "Unable to load agreement document:",
                error
            );

            $("#cab-hub-agreement-loading")
                .text(
                    "Agreement document unavailable"
                );
        }
    }
}


export class CabHubConnectionSurveyHistoryTable {

    private readonly tableId =
        "connection-survey-history-table";

    private dataTable:
        any;

    private readonly tableRenderer:
        TableRenderer;


    constructor(
        private readonly rows:
            CabHubSurveyHistoryRow[],

        private readonly pickLists:
            JsonItem[]
    ) {
        this.tableRenderer =
            new TableRenderer(
                this.pickLists
            );
    }


    public initialize():
        void {

        this.dataTable =
            $(`#${this.tableId}`)
                .DataTable({
                    data:
                        this.rows,

                    paging:
                        false,

                    searching:
                        false,

                    info:
                        false,

                    ordering:
                        true,

                    scrollX:
                        false,

                    autoWidth:
                        true,

                    dom:
                        '<"connection-survey-history-controls d-flex align-items-center justify-content-end mb-2"B>rt',

                    buttons: [
                        {
                            extend:
                                "csv",

                            text:
                                '<i class="fas fa-file-download"></i>',

                            titleAttr:
                                "CSV",
                       
                            charset:
                                "utf-8"
                        }
                    ],

                    language: {
                        emptyTable:
                            "No surveys have been submitted for this connection."
                    },

                    columns: [
                        {
                            data:
                                "SurveyType",

                            title:
                                "Survey Type",

                            className:
                                "text-left font-size14 nowrap"
                        },
                        {
                            data:
                                "MonthYearDisplay",

                            title:
                                "Month / Year",

                            className:
                                "text-left font-size14 nowrap"
                        },
                        {
                            data:
                                "HoursWorked",

                            title:
                                "Hours",

                            className:
                                "text-left font-size14 nowrap",

                            render:
                                (
                                    value:
                                        number
                                ): string => {

                                    if (
                                        value === null ||
                                        value === undefined
                                    ) {
                                        return "—";
                                    }

                                    return String(
                                        value
                                    );
                                }
                        },
                        {
                            data:
                                "MetThisMonth",

                            title:
                                "Met This Month",

                            className:
                                "text-center font-size14 nowrap",

                            render:
                                (
                                    value:
                                        boolean,
                                    type:
                                        string
                                ): string => {

                                    const text =
                                        value
                                            ? "Yes"
                                            : "No";

                                    if (
                                        type !==
                                        "display"
                                    ) {
                                        return text;
                                    }

                                    return `
                                        <span class="
                                            ${value
                                            ? "cab-edit-table-active-pill"
                                            : "cab-detail-table-inactive-pill"}
                                        ">
                                            ${text}
                                        </span>
                                    `;
                                }
                        },
                        {
                            data:
                                "SatisfactionRatingId",

                            title:
                                "Satisfaction",

                            className:
                                "text-left font-size14 nowrap",

                            render:
                                (
                                    value:
                                        number
                                ): string => {

                                    if (!value) {
                                        return "—";
                                    }

                                    return this.tableRenderer
                                        .renderDisplay(
                                            value,
                                            TableRenderType.Number,
                                            "SatisfactionRating"
                                        );
                                }
                        },
                        {
                            data:
                                "SupportRequested",

                            title:
                                "Support Requested",

                            className:
                                "text-center font-size14 nowrap",

                            render:
                                (
                                    value:
                                        boolean,
                                    type:
                                        string
                                ): string => {

                                    const text =
                                        value
                                            ? "Yes"
                                            : "No";

                                    if (
                                        type !==
                                        "display"
                                    ) {
                                        return text;
                                    }

                                    return `
                                        <span class="
                                            ${value
                                            ? "cab-edit-table-active-pill"
                                            : "cab-detail-table-inactive-pill"}
                                        ">
                                            ${text}
                                        </span>
                                    `;
                                }
                        },
                        {
                            data:
                                "SubmittedDate",

                            title:
                                "Submitted Date",

                            className:
                                "text-left font-size14 nowrap",

                            render:
                                (
                                    value:
                                        string,
                                    type:
                                        string
                                ): string => {

                                    if (!value) {
                                        return "";
                                    }

                                    return type ===
                                        "display"
                                        ? getDisplayDate(
                                            new Date(
                                                value
                                            )
                                        )
                                        : value;
                                }
                        }
                    ],

                    order: [
                        [
                            6,
                            "desc"
                        ]
                    ]
                });
    }
}